/**
 * @file React hook — useEnabledStages.ts: form/state logic for contributor dashboard.
 */

/**
 * useEnabledStages
 *
 * Flow: Contributor Details → Publication Details → Background Data → [A1|A2|A3|A1-A3]* → Dataset
 *
 * Fixed stages (always): Contributor's Details, Publication Details, Background Data
 *
 * After Background Data, only the TICKED stages appear, in order:
 * - A1 only → goes to A1
 * - A2 only → goes to A2 directly (skips A1)
 * - A3 only → goes to A3 directly
 * - A1-A3 only → goes to A1-A3 directly
 * - A2 + A3 → goes to A2, then A3 (skips A1)
 * - Any combination → only selected stages in order A1, A2, A3, A1-A3
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { getSubmissionById } from "../../../../services/submissionService";
import { BASE_STAGES, type StageName } from "../types/contributorStageNames";
import { mapApiCurrentStageToStageName } from "../utils/mapApiStageToContributorSection";

export type { StageName } from "../types/contributorStageNames";

/** API may send booleans or string "true"/"false"; avoid truthy strings. */
export function backgroundFlagTrue(v: unknown): boolean {
  return v === true || v === "true" || v === 1 || v === "1";
}

/** Build ordered stage list from background LCA flags (same rules as the contributor wizard). */
export function buildEnabledStagesFromBackground(
  bg: {
    a1?: boolean;
    a2?: boolean;
    a3?: boolean;
    a1_a3?: boolean;
  } | null | undefined
): StageName[] {
  const stages: StageName[] = [...BASE_STAGES];

  if (!bg) {
    stages.push("Submitted Dataset");
    return stages;
  }

  const a1 = backgroundFlagTrue(bg.a1);
  const a2 = backgroundFlagTrue(bg.a2);
  const a3 = backgroundFlagTrue(bg.a3);
  const a1_a3 = backgroundFlagTrue(bg.a1_a3);

  if (a1) stages.push("A1");
  if (a2) stages.push("A2");
  if (a3) stages.push("A3");
  if (a1_a3) stages.push("A1-A3");

  const hasAnyLca = a1 || a2 || a3 || a1_a3;
  if (hasAnyLca) stages.push("Dataset");
  stages.push("Submitted Dataset");

  return stages;
}

/**
 * If resume target (e.g. A2) is missing from the list because background flags were absent or wrong,
 * merge flags so the sidebar index matches the server-reported current stage.
 */
export function mergeBackgroundToIncludeStage(
  bg: Parameters<typeof buildEnabledStagesFromBackground>[0],
  target: StageName
): {
  a1: boolean;
  a2: boolean;
  a3: boolean;
  a1_a3: boolean;
} {
  const base = {
    a1: backgroundFlagTrue(bg?.a1),
    a2: backgroundFlagTrue(bg?.a2),
    a3: backgroundFlagTrue(bg?.a3),
    a1_a3: backgroundFlagTrue(bg?.a1_a3),
  };
  switch (target) {
    case "A1":
      base.a1 = true;
      break;
    case "A2":
      base.a2 = true;
      break;
    case "A3":
      base.a3 = true;
      break;
    case "A1-A3":
      base.a1_a3 = true;
      break;
    case "Dataset":
      if (!base.a1 && !base.a2 && !base.a3 && !base.a1_a3) base.a1 = true;
      break;
    default:
      break;
  }
  return base;
}

const progressWithoutSubmitted = (list: StageName[]) =>
  list.filter((s) => s !== "Submitted Dataset");

/**
 * Stage list from saved background, aligned with server `currentStage` when background flags are missing or stale.
 */
export function buildContributorStageListFromSubmission(
  submission: {
    background?: Parameters<typeof buildEnabledStagesFromBackground>[0];
    currentStage?: string;
    current_stage?: string;
  } | null,
  apiStageOverride?: string | null
): StageName[] {
  if (!submission) {
    return buildEnabledStagesFromBackground(null);
  }
  const bg = submission.background;
  let stages = buildEnabledStagesFromBackground(bg);

  const a1 = backgroundFlagTrue(bg?.a1);
  const a2 = backgroundFlagTrue(bg?.a2);
  const a3 = backgroundFlagTrue(bg?.a3);
  const a1_a3 = backgroundFlagTrue(bg?.a1_a3);
  const hasAnyLca = a1 || a2 || a3 || a1_a3;

  const apiStage =
    apiStageOverride ??
    submission.currentStage ??
    submission.current_stage;
  const target = mapApiCurrentStageToStageName(apiStage);
  if (target && target !== "Submitted Dataset") {
    if (!progressWithoutSubmitted(stages).includes(target)) {
      // Trust saved background checkboxes when the contributor already chose at least
      // one LCA stage. Do not force-merge `currentStage` (often still A1) over explicit
      // false flags — that incorrectly sent users to A1 when only A2/A3/A1-A3 was selected.
      if (!hasAnyLca) {
        stages = buildEnabledStagesFromBackground(
          mergeBackgroundToIncludeStage(bg, target)
        );
      }
    }
  }
  return stages;
}

/** Hook: use Enabled Stages. */
export function useEnabledStages(submissionId: string | null) {
  const [enabledStages, setEnabledStages] = useState<StageName[]>(() =>
    buildEnabledStagesFromBackground(null)
  );
  const [loading, setLoading] = useState(false);
  const stagesRef = useRef<StageName[]>(enabledStages);
  stagesRef.current = enabledStages;

  const refetch = useCallback(async (): Promise<StageName[]> => {
    if (!submissionId) return stagesRef.current;
    setLoading(true);
    try {
      const res = await getSubmissionById(submissionId);
      if (res?.data?.success && res.data.data) {
        const stages = buildContributorStageListFromSubmission(
          res.data.data
        );
        setEnabledStages(stages);
        return stages;
      }
    } catch (err) {
      console.error("Failed to fetch submission for enabled stages:", err);
    } finally {
      setLoading(false);
    }
    return stagesRef.current;
  }, [submissionId]);

  useEffect(() => {
    if (!submissionId) {
      setEnabledStages(buildEnabledStagesFromBackground(null));
      return;
    }
    refetch();
  }, [submissionId, refetch]);

  return { enabledStages, loading, refetch, syncEnabledStages: setEnabledStages };
}
